import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';
import { ArrowUpRight, Clock, ArrowLeft, List, BookOpen, Share2, ChevronUp } from 'lucide-react';

const renderMarkdownLite = (text) => {
  if (!text) return null;
  const parts = [];
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={parts.length} className="text-[#143324] font-semibold">{m[1]}</strong>);
    } else if (m[2] && m[3]) {
      const url = m[3];
      if (url.startsWith('/')) {
        parts.push(
          <Link key={parts.length} to={url} className="text-[#143324] underline decoration-[#143324]/40 underline-offset-2 hover:decoration-[#143324]">
            {m[2]}
          </Link>
        );
      } else {
        parts.push(
          <a key={parts.length} href={url} target="_blank" rel="noreferrer" className="text-[#143324] underline decoration-[#143324]/40 underline-offset-2 hover:decoration-[#143324]">
            {m[2]}
          </a>
        );
      }
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = useMemo(() => blogPosts.find((p) => p.slug === slug), [slug]);
  const related = useMemo(
    () => (post?.related || []).map((s) => blogPosts.find((p) => p.slug === s)).filter(Boolean),
    [post]
  );
  const [activeSection, setActiveSection] = useState(post?.sections?.[0]?.id);

  // Scroll spy
  useEffect(() => {
    if (!post) return;
    const handler = () => {
      const ids = post.sections.map((s) => s.id);
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 200) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-36 pb-10 md:pb-14 px-3 md:px-6">
        <div className="squiggle-bg" />
        <div className="max-w-[1200px] mx-auto">
          <div className="px-6 md:px-4 mb-6">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[#143324] font-body hover:gap-2.5 transition-all">
              <ArrowLeft size={14} /> Back to all articles
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-start px-6 md:px-4">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-5 text-xs text-[#3d3d3d] font-body flex-wrap">
                <span className="px-2.5 py-1 rounded-full border border-[#143324]/15 text-[#143324]">{post.tag}</span>
                <span>{post.date}</span>
                {post.readTime && (
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {post.readTime}</span>
                )}
              </div>
              <h1 className="font-serif-display text-[#111111] text-[38px] md:text-[52px] lg:text-[60px] leading-[1.04] tracking-[-0.015em]">
                {post.title}
              </h1>
              <p className="mt-5 text-[#3d3d3d] font-body text-[15px] md:text-[16px] leading-relaxed max-w-2xl">
                {post.excerpt}
              </p>
              {post.tags && (
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {post.tags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-full bg-white/70 border border-[#143324]/10 text-[11px] font-body text-[#143324]">#{t}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden shadow-md">
                <img src={post.image} alt={post.title} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover aspect-[4/3]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body: TOC + Article + Related */}
      <section className="pb-16 md:pb-24 px-3 md:px-6">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 px-6 md:px-4">
          {/* TOC (sticky) */}
          <aside className="lg:col-span-1 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="card-soft rounded-2xl p-5">
                <p className="text-[10.5px] tracking-wider uppercase text-[#143324] font-body font-medium mb-3 flex items-center gap-1.5">
                  <List size={12} /> On this page
                </p>
                <ul className="space-y-2">
                  {post.sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(s.id);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className={`block text-[12.5px] font-body leading-snug transition-colors ${
                          activeSection === s.id ? 'text-[#143324] font-medium' : 'text-[#3d3d3d] hover:text-[#143324]'
                        }`}
                      >
                        {s.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.href);
                }}
                className="w-full inline-flex items-center gap-2 justify-center btn-outline px-4 py-2.5 rounded-full text-[12.5px] font-body"
              >
                <Share2 size={13} /> Copy article link
              </button>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-3 order-1 lg:order-2">
            {post.sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-28 mb-10">
                <h2 className="font-serif-display text-[#143324] text-2xl md:text-3xl leading-tight mb-4">
                  <span className="text-[#3d3d3d]/60 font-body text-[13px] tracking-wider uppercase mr-3">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {s.heading}
                </h2>
                <div className="space-y-4 text-[#1c1c1c] font-body leading-relaxed text-[15px]">
                  {s.body.map((para, pi) => (
                    <p key={pi}>{renderMarkdownLite(para)}</p>
                  ))}
                </div>
              </section>
            ))}

            {/* End CTA */}
            <div className="card-soft rounded-2xl p-6 md:p-8 mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <div>
                <h4 className="font-serif-display text-[#143324] text-2xl mb-1">Ready for a clearer financial conversation?</h4>
                <p className="text-[#3d3d3d] font-body text-[13.5px]">Tell AJ Tax Consulting about the decisions and priorities facing your business.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-[13px] font-body">
                  Request a Consultation <ArrowUpRight size={13} />
                </Link>
              </div>
            </div>
          </article>

          {/* Related sidebar */}
          <aside className="lg:col-span-1 order-3">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="card-soft rounded-2xl p-5">
                <p className="text-[10.5px] tracking-wider uppercase text-[#143324] font-body font-medium mb-4 flex items-center gap-1.5">
                  <BookOpen size={12} /> Related
                </p>
                <ul className="space-y-4">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/blog/${r.slug}`} className="group block">
                        <div className="aspect-[4/3] rounded-lg overflow-hidden mb-2">
                          <img src={r.image} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        </div>
                        <p className="text-[11px] text-[#3d3d3d] font-body mb-1">{r.tag} · {r.readTime || ''}</p>
                        <p className="font-body font-semibold text-[13px] text-[#143324] leading-snug">{r.title}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="w-full inline-flex items-center gap-2 justify-center btn-outline px-4 py-2.5 rounded-full text-[12.5px] font-body"
              >
                <ChevronUp size={13} /> Back to top
              </button>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
